import { Plus, X } from "lucide-react";
import PropTypes from "prop-types";
import DefaultItemsEditor from "../../../components/DefaultItemsEditor";
import { Input, Select, TextArea } from "../../../components/FormComponents";
import SectionEditor from "../../../components/SectionEditor";
import SectionToolbar, {
	SectionActions,
} from "../../../components/SectionToolbar";
import { ICON_OPTIONS } from "../../../constants/themes";

export default function TabContent({
	formData,
	setFormData,
	showNotification,
	collapsedSections,
	setCollapsedSections,
}) {
	const cat = formData.category;
	const showHeroEditor = cat === "full" || cat === "hero" || cat === "products";
	const showSectionsEditor =
		cat === "full" ||
		cat === "content" ||
		cat === "footer" ||
		cat === "products";
	const showProductsEditor = cat === "full" || cat === "products";

	// Section Helpers
	const handleAddSection = (newSection) => {
		setFormData((prev) => ({
			...prev,
			sections: [...prev.sections, newSection],
		}));
	};

	const handleSectionChange = (idx, field, value) => {
		setFormData((prev) => {
			const newSections = [...prev.sections];
			newSections[idx] = { ...newSections[idx], [field]: value };
			return { ...prev, sections: newSections };
		});
	};

	const handleRemoveSection = (idx) => {
		setFormData((prev) => ({
			...prev,
			sections: prev.sections.filter((_, i) => i !== idx),
		}));
	};

	const handleMoveSection = (fromIdx, toIdx) => {
		if (toIdx < 0 || toIdx >= formData.sections.length) return;
		setFormData((prev) => {
			const newSections = [...prev.sections];
			const [moved] = newSections.splice(fromIdx, 1);
			newSections.splice(toIdx, 0, moved);
			return { ...prev, sections: newSections };
		});
	};

	const handleDuplicateSection = (idx) => {
		const original = formData.sections[idx];
		if (
			original.type === "company_intro" &&
			formData.sections.filter((s) => s.type === "company_intro").length >= 1
		) {
			showNotification('Ja existe uma secao "Quem Somos" (maximo 1)', "error");
			return;
		}
		setFormData((prev) => {
			const newSections = [...prev.sections];
			newSections.splice(idx + 1, 0, JSON.parse(JSON.stringify(original)));
			return { ...prev, sections: newSections };
		});
	};

	const toggleCollapsed = (idx) => {
		setCollapsedSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
	};

	return (
		<div className="space-y-5 animate-in fade-in slide-in-from-bottom-1 duration-300">
			<TextArea
				label="Descrição Interna"
				value={formData.description}
				onChange={(e) =>
					setFormData({ ...formData, description: e.target.value })
				}
				placeholder="Para que serve este template..."
				rows="2"
			/>

			{/* Hero Cards Editor */}
			{showHeroEditor && (
				<div className="border-t border-white/5 pt-4">
					<div className="flex items-center justify-between mb-3">
						<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">
							Hero Cards ({(formData.hero_cards || []).length})
						</h3>
						<button
							type="button"
							onClick={() =>
								setFormData({
									...formData,
									hero_cards: [
										...(formData.hero_cards || []),
										{ icon: "star", color: "cyan", title: "", subtitle: "" },
									],
								})
							}
							className="text-[10px] bg-slate-800 hover:bg-slate-700 text-cyan-400 px-2 py-1 rounded border border-white/5 transition-colors flex items-center gap-1"
						>
							<Plus className="w-3 h-3" /> Adicionar
						</button>
					</div>
					{(formData.hero_cards || []).map((card, ci) => (
						<div
							key={ci}
							className="p-3 bg-slate-900/40 rounded-xl border border-white/5 mb-3 space-y-3"
						>
							<div className="flex items-center justify-between pb-1 border-b border-white/5">
								<span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">
									Card #{ci + 1}
								</span>
								<button
									type="button"
									onClick={() =>
										setFormData({
											...formData,
											hero_cards: formData.hero_cards.filter(
												(_, i) => i !== ci,
											),
										})
									}
									className="text-slate-600 hover:text-red-400 transition-colors"
								>
									<X className="w-3.5 h-3.5" />
								</button>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<Select
									label="Ícone"
									value={card.icon || ""}
									onChange={(e) => {
										const updated = [...formData.hero_cards];
										updated[ci] = { ...updated[ci], icon: e.target.value };
										setFormData({ ...formData, hero_cards: updated });
									}}
									options={ICON_OPTIONS.filter((o) => o.value)}
								/>
								<Select
									label="Cor"
									value={card.color || "cyan"}
									onChange={(e) => {
										const updated = [...formData.hero_cards];
										updated[ci] = { ...updated[ci], color: e.target.value };
										setFormData({ ...formData, hero_cards: updated });
									}}
									options={[
										{ value: "cyan", label: "Cyan" },
										{ value: "purple", label: "Purple" },
										{ value: "emerald", label: "Emerald" },
										{ value: "blue", label: "Blue" },
										{ value: "amber", label: "Amber" },
									]}
								/>
							</div>
							<div className="space-y-3">
								<Input
									label="Título"
									value={card.title || ""}
									onChange={(e) => {
										const updated = [...formData.hero_cards];
										updated[ci] = { ...updated[ci], title: e.target.value };
										setFormData({ ...formData, hero_cards: updated });
									}}
									placeholder="Ex: {{company_name}}"
								/>
								<TextArea
									label="Subtítulo"
									value={card.subtitle || ""}
									onChange={(e) => {
										const updated = [...formData.hero_cards];
										updated[ci] = { ...updated[ci], subtitle: e.target.value };
										setFormData({ ...formData, hero_cards: updated });
									}}
									placeholder="Ex: {{company_legal_name}}"
									rows="1"
								/>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Sections Editor */}
			{showSectionsEditor && (
				<div className="border-t border-white/5 pt-4">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">
							Seções do Template
						</h3>
						<SectionToolbar
							sections={formData.sections}
							onAdd={handleAddSection}
							onNotify={showNotification}
						/>
					</div>

					{formData.sections.map((sec, idx) => (
						<div key={idx} className="mb-4">
							<div className="flex items-center gap-1 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
								<SectionActions
									index={idx}
									total={formData.sections.length}
									onMove={handleMoveSection}
									onDuplicate={handleDuplicateSection}
									onRemove={handleRemoveSection}
								/>
							</div>
							<SectionEditor
								section={sec}
								index={idx}
								onChange={(field, value) =>
									handleSectionChange(idx, field, value)
								}
								onRemove={() => handleRemoveSection(idx)}
								collapsed={!!collapsedSections[idx]}
								onToggle={() => toggleCollapsed(idx)}
							/>
						</div>
					))}
				</div>
			)}

			{/* Products Editor */}
			{showProductsEditor && (
				<div className="border-t border-white/5 pt-4">
					<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-3">
						Planos & Módulos Padrão
					</h3>
					<DefaultItemsEditor
						items={formData.default_items || []}
						onChange={(items) =>
							setFormData({ ...formData, default_items: items })
						}
						companyId={formData.company_id}
					/>
				</div>
			)}
		</div>
	);
}

TabContent.propTypes = {
	formData: PropTypes.object.isRequired,
	setFormData: PropTypes.func.isRequired,
	showNotification: PropTypes.func.isRequired,
	collapsedSections: PropTypes.object.isRequired,
	setCollapsedSections: PropTypes.func.isRequired,
};
